<template>
  <div class="dashboard-motorista">

    <!-- Header / Perfil do Motorista -->
    <div class="profile-section mb-4">
      <div class="profile-card">
        <div class="d-flex align-items-center mb-2">
          <b-avatar
            :text="getIniciais(motorista.NOME)"
            size="3.8rem"
            class="profile-avatar shadow-sm mr-3"
            variant="info"
          />
          <div class="profile-info">
            <h4 class="name mb-0">{{ motorista.NOME || 'Motorista' }}</h4>
            <div class="meta-label">
              ORDENS: <span>{{ ordens.length }}</span>
              &nbsp;|&nbsp;
              ENTREGUES: <span>{{ totalEntregues }}</span>
            </div>
          </div>
        </div>
        <div class="progress-container" v-if="totalPedidos > 0">
          <div class="d-flex justify-content-between mb-1 small">
            <span>Pedidos entregues</span>
            <span class="font-weight-bold">{{ percentualEntregue.toFixed(0) }}%</span>
          </div>
          <b-progress
            class="progress-modern"
            height="10px"
            :variant="percentualEntregue >= 100 ? 'success' : 'info'"
            :value="percentualEntregue"
            :max="100"
            animated
          />
        </div>
      </div>
    </div>

    <!-- Sem funcionário vinculado -->
    <div v-if="!funcionarioId" class="text-center p-5 text-muted bg-light rounded shadow-sm">
      <i class="fa fa-exclamation-triangle fa-3x mb-3 text-warning"></i>
      <p class="font-weight-bold">Nenhum funcionário vinculado ao seu usuário.</p>
      <p>Solicite ao administrador que vincule seu usuário a um funcionário motorista.</p>
    </div>

    <!-- Lista de Ordens -->
    <div v-else>
      <!-- Cabeçalho + Filtro -->
      <div class="section-header d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">Ordens de Entrega</h5>
        <div class="d-flex align-items-center">
          <b-button size="sm" pill variant="outline-info" @click="carregarOrdens" :disabled="carregando" class="mr-2">
            <i :class="carregando ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
          </b-button>
          <b-button
            size="sm" pill
            :variant="filtroStatus ? 'primary' : 'outline-secondary'"
            @click="$bvModal.show('modal-filtro-status')"
          >
            <i class="fa fa-filter mr-1"></i>{{ filtroStatusLabel }}
          </b-button>
        </div>
      </div>

      <div v-if="ordens.length === 0 && !carregando" class="text-center p-5 text-muted bg-light rounded shadow-sm">
        <i class="fa fa-truck fa-3x mb-3"></i>
        <p>Nenhuma ordem de entrega com este filtro.</p>
      </div>

      <!-- Card de cada Ordem -->
      <div
        v-for="ordem in ordens"
        :key="getOrdemKey(ordem)"
        class="ordem-card mb-3 shadow-sm"
      >
        <!-- Cabeçalho da Ordem -->
        <div :class="['ordem-card-header', 'status-' + getStatusVariant(ordem.STATUS)]"
          @click="toggleOrdem(ordem)"
        >
          <div class="d-flex align-items-center">
            <i class="fa fa-truck mr-2"></i>
            <div>
              <span class="ordem-numero">Ordem #{{ ordem.NUMERO }}</span>
              <span class="ordem-data ml-2 text-muted small">{{ formatarData(ordem.DATA) }}</span>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <b-badge :variant="getStatusVariant(ordem.STATUS)" class="mr-2">
              {{ ordem.STATUS_TRADUZIDO }}
            </b-badge>
            <span class="text-muted small mr-2">{{ contarEntregues(ordem) }}/{{ (pedidosPorOrdem[getOrdemKey(ordem)] || []).length || ordem.TOTAL_PEDIDOS }} ped.</span>
            <i :class="ordemAberta(ordem) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
          </div>
        </div>

        <!-- Placa -->
        <div class="ordem-placa px-3 py-1" v-if="ordemAberta(ordem)">
          <small class="text-muted">
            <i class="fa fa-car mr-1"></i> Veículo: <strong>{{ ordem.PLACA || 'Não informado' }}</strong>
          </small>
        </div>

        <!-- Pedidos da Ordem -->
        <div v-if="ordemAberta(ordem)" class="pedidos-list">
          <div v-if="!pedidosPorOrdem[getOrdemKey(ordem)]" class="text-center py-3 text-muted small">
            <i class="fa fa-spinner fa-spin"></i> Carregando pedidos...
          </div>
          <div v-else-if="pedidosPorOrdem[getOrdemKey(ordem)].length === 0" class="text-center py-3 text-muted small">
            Nenhum pedido nesta ordem.
          </div>

          <!-- Card de cada Pedido -->
          <div
            v-for="pedido in pedidosPorOrdem[getOrdemKey(ordem)]"
            :key="pedido.SEQUENCIA"
            :class="['pedido-card mb-2 mx-2', { 'pedido-entregue': isEntregue(pedido) }]"
          >
            <!-- Status header -->
            <div :class="['pedido-header', isEntregue(pedido) ? 'status-success' : 'status-' + getSituacaoVariant(pedido)]">
              <div class="d-flex align-items-center">
                <i v-if="isEntregue(pedido)" class="fa fa-check-circle mr-1"></i>
                <span class="pedido-situacao">{{ isEntregue(pedido) ? 'ENTREGUE' : pedido.SITUACAO_TRADUZIDA }}</span>
              </div>
              <div class="d-flex align-items-center">
                <!-- Badge ocorrências clicável -->
                <span
                  :class="['badge-ocorrencia mr-1', pedido.QTD_OCORRENCIAS > 0 ? 'has-items' : '']"
                  @click.stop="abrirListaOcorrencias(ordem, pedido)"
                  title="Ver/excluir ocorrências"
                >
                  <i class="fa fa-exclamation-circle"></i> {{ pedido.QTD_OCORRENCIAS }}
                </span>
                <!-- Badge documentos clicável -->
                <span
                  :class="['badge-doc', pedido.QTD_DOCUMENTOS > 0 ? 'has-items' : '']"
                  @click.stop="abrirListaDocumentos(ordem, pedido)"
                  title="Ver/excluir documentos"
                >
                  <i class="fa fa-paperclip"></i> {{ pedido.QTD_DOCUMENTOS }}
                </span>
              </div>
            </div>

            <div class="pedido-body">
              <div class="pedido-cliente">{{ pedido.NOME_CLIENTE }}</div>
              <div class="pedido-info">
                <span class="pedido-id">Pedido #{{ pedido.SEQUENCIA }}</span>
                <span v-if="pedido.NENTREGA" class="pedido-entrega ml-2">
                  <i class="fa fa-truck mr-1"></i>Ent. {{ pedido.NENTREGA }}
                </span>
              </div>
              <div v-if="pedido.ENDERECO" class="pedido-endereco text-muted small">
                <i class="fa fa-map-marker mr-1"></i>{{ pedido.ENDERECO }}<span v-if="pedido.CIDADE">, {{ pedido.CIDADE }}</span>
              </div>
              <div v-if="pedido.DT_RETORNO_EXPEDICAO" class="mt-1">
                <b-badge variant="success" size="sm">
                  <i class="fa fa-check mr-1"></i>Entregue em {{ formatarDataHora(pedido.DT_RETORNO_EXPEDICAO) }}
                </b-badge>
              </div>
              <div v-else-if="pedido.ULTIMA_OCORRENCIA" class="pedido-ult-ocorrencia mt-1">
                <b-badge variant="warning" size="sm">Últ. ocor.: {{ pedido.ULTIMA_OCORRENCIA }}</b-badge>
              </div>

              <!-- Ações por Pedido -->
              <div class="pedido-acoes mt-2">
                <b-button
                  v-if="!isEntregue(pedido)"
                  size="sm"
                  variant="success"
                  class="mr-1"
                  @click="confirmarEntrega(ordem, pedido)"
                >
                  <i class="fa fa-check"></i> Entregar
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-warning"
                  class="mr-1"
                  @click="abrirModalOcorrencia(ordem, pedido)"
                >
                  <i class="fa fa-exclamation-circle"></i> Ocorrência
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="abrirModalDocumento(ordem, pedido)"
                >
                  <i class="fa fa-camera"></i> Canhoto
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <!-- Confirmar Entrega Total da Ordem -->
        <div
          v-if="ordemAberta(ordem) && ordem.TIPO_ORDEM !== 'ENTREGA' && ordem.STATUS !== '5' && pedidosPorOrdem[getOrdemKey(ordem)] && pedidosPorOrdem[getOrdemKey(ordem)].length > 0 && contarEntregues(ordem) >= pedidosPorOrdem[getOrdemKey(ordem)].length"
          class="px-3 py-2 border-top text-right"
        >
          <b-button size="sm" variant="success" @click.stop="confirmarEntregaOrdem(ordem)">
            <i class="fa fa-flag-checkered mr-1"></i> Finalizar Entrega da Ordem
          </b-button>
        </div>
        <div v-else-if="ordemAberta(ordem) && ordem.STATUS === '5'" class="px-3 py-2 border-top text-right">
          <b-badge variant="success" pill><i class="fa fa-check mr-1"></i>Ordem totalmente entregue</b-badge>
        </div>
      </div>
    </div>

    <!-- ===== Modal: Filtro de Status ===== -->
    <b-modal
      id="modal-filtro-status"
      title="Filtrar Ordens"
      centered
      hide-footer
      header-bg-variant="light"
    >
      <div class="p-2">
        <b-form-group label="Status da Ordem" label-class="font-weight-bold">
          <b-form-select v-model="filtroStatusTemp" :options="statusOptions" class="modern-select" />
        </b-form-group>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="light" class="mr-2" @click="$bvModal.hide('modal-filtro-status')">Fechar</b-button>
          <b-button variant="primary" @click="aplicarFiltro">Aplicar</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== Modal: Registrar Ocorrência ===== -->
    <b-modal
      id="modal-ocorrencia"
      ref="modal-ocorrencia"
      title="Registrar Ocorrência"
      centered
      hide-footer
      header-bg-variant="warning"
    >
      <div class="p-1" v-if="ocorrencia.pedido">
        <div class="mb-3">
          <strong>{{ ocorrencia.pedido.NOME_CLIENTE }}</strong>
          <span class="text-muted small ml-2">Pedido #{{ ocorrencia.pedido.SEQUENCIA }}</span>
          <span v-if="ocorrencia.pedido.NENTREGA" class="text-muted small ml-2">| Ent. {{ ocorrencia.pedido.NENTREGA }}</span>
        </div>

        <b-form-group label="Tipo de Ocorrência *" label-class="font-weight-bold">
          <b-form-select
            v-model="ocorrencia.tipo"
            :options="tiposOcorrencia"
            :state="ocorrencia.tipo ? true : null"
          />
        </b-form-group>

        <b-form-group label="Descrição (opcional)" label-class="font-weight-bold">
          <b-form-textarea
            v-model="ocorrencia.descricao"
            rows="3"
            placeholder="Descreva o que ocorreu..."
            maxlength="500"
          />
          <small class="text-muted">{{ (ocorrencia.descricao || '').length }}/500</small>
        </b-form-group>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('modal-ocorrencia')">
            Cancelar
          </b-button>
          <b-button
            variant="warning"
            :disabled="!ocorrencia.tipo || salvandoOcorrencia"
            @click="salvarOcorrencia"
          >
            <i :class="salvandoOcorrencia ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i>
            {{ salvandoOcorrencia ? 'Salvando...' : 'Registrar' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== Modal: Enviar Documento ===== -->
    <b-modal
      id="modal-documento"
      ref="modal-documento"
      title="Enviar Documento"
      centered
      hide-footer
      header-bg-variant="primary"
      header-text-variant="white"
    >
      <div class="p-1" v-if="documento.pedido">
        <div class="mb-3">
          <strong>{{ documento.pedido.NOME_CLIENTE }}</strong>
          <span class="text-muted small ml-2">Pedido #{{ documento.pedido.SEQUENCIA }}</span>
          <span v-if="documento.pedido.NENTREGA" class="text-muted small ml-2">| Ent. {{ documento.pedido.NENTREGA }}</span>
        </div>

        <b-form-group label="Tipo de Documento" label-class="font-weight-bold">
          <b-form-select v-model="documento.tipoDoc" :options="tiposDocumento" />
        </b-form-group>

        <b-form-group label="Foto / Arquivo *" label-class="font-weight-bold">
          <b-form-file
            v-model="documento.arquivo"
            accept="image/*,application/pdf"
            capture="environment"
            placeholder="Tire uma foto ou escolha um arquivo..."
            browse-text="Selecionar"
            @change="onArquivoChange"
          />
        </b-form-group>

        <div v-if="documento.preview" class="text-center mb-3">
          <img
            v-if="documento.mimeType && documento.mimeType.startsWith('image')"
            :src="documento.preview"
            class="img-preview img-fluid rounded shadow-sm"
          />
          <div v-else class="pdf-preview p-3 bg-light rounded text-center">
            <i class="fa fa-file-pdf-o fa-3x text-danger"></i>
            <p class="mt-2 small">{{ documento.arquivo ? documento.arquivo.name : '' }}</p>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('modal-documento')">
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!documento.arquivo || enviandoDocumento"
            @click="enviarDocumento"
          >
            <i :class="enviandoDocumento ? 'fa fa-spinner fa-spin' : 'fa fa-upload'"></i>
            {{ enviandoDocumento ? 'Enviando...' : 'Enviar' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== Modal: Lista de Ocorrências ===== -->
    <b-modal
      id="modal-lista-ocorrencias"
      ref="modal-lista-ocorrencias"
      title="Ocorrências do Pedido"
      centered
      hide-footer
      size="lg"
    >
      <div v-if="listaOcorrencias.pedido" class="p-1">
        <div class="mb-3">
          <strong>{{ listaOcorrencias.pedido.NOME_CLIENTE }}</strong>
          <span class="text-muted small ml-2">Pedido #{{ listaOcorrencias.pedido.SEQUENCIA }}</span>
          <span v-if="listaOcorrencias.pedido.NENTREGA" class="text-muted small ml-2">| Ent. {{ listaOcorrencias.pedido.NENTREGA }}</span>
        </div>

        <div v-if="listaOcorrencias.carregando" class="text-center py-3">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
        <div v-else-if="listaOcorrencias.itens.length === 0" class="text-center text-muted py-3">
          Nenhuma ocorrência registrada.
        </div>
        <div v-else>
          <div
            v-for="oc in listaOcorrencias.itens"
            :key="oc.ID"
            class="ocorrencia-item d-flex justify-content-between align-items-start mb-2 p-2 rounded"
          >
            <div>
              <b-badge :variant="oc.TIPO_OCORRENCIA === 'ENTREGUE' ? 'success' : 'warning'" class="mr-2">
                {{ oc.TIPO_OCORRENCIA }}
              </b-badge>
              <span class="small text-muted">{{ formatarDataHora(oc.DT_OCORRENCIA) }}</span>
              <div v-if="oc.DESCRICAO" class="small mt-1">{{ oc.DESCRICAO }}</div>
              <div v-if="oc.USUARIO" class="small text-muted">por {{ oc.USUARIO }}</div>
            </div>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="excluirOcorrencia(oc)"
              :disabled="oc.excluindo || oc.SOMENTE_LEITURA"
            >
              <i :class="oc.excluindo ? 'fa fa-spinner fa-spin' : 'fa fa-trash'"></i>
            </b-button>
          </div>
        </div>

        <div class="d-flex justify-content-between mt-3">
          <b-button size="sm" variant="outline-warning" @click="abrirModalOcorrencia(listaOcorrencias.ordem, listaOcorrencias.pedido); $bvModal.hide('modal-lista-ocorrencias')">
            <i class="fa fa-plus"></i> Nova ocorrência
          </b-button>
          <b-button variant="secondary" @click="$bvModal.hide('modal-lista-ocorrencias')">Fechar</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== Modal: Lista de Documentos ===== -->
    <b-modal
      id="modal-lista-documentos"
      ref="modal-lista-documentos"
      title="Documentos do Pedido"
      centered
      hide-footer
      size="lg"
    >
      <div v-if="listaDocumentos.pedido" class="p-1">
        <div class="mb-3">
          <strong>{{ listaDocumentos.pedido.NOME_CLIENTE }}</strong>
          <span class="text-muted small ml-2">Pedido #{{ listaDocumentos.pedido.SEQUENCIA }}</span>
          <span v-if="listaDocumentos.pedido.NENTREGA" class="text-muted small ml-2">| Ent. {{ listaDocumentos.pedido.NENTREGA }}</span>
        </div>

        <div v-if="listaDocumentos.carregando" class="text-center py-3">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
        <div v-else-if="listaDocumentos.itens.length === 0" class="text-center text-muted py-3">
          Nenhum documento enviado.
        </div>
        <div v-else>
          <div
            v-for="doc in listaDocumentos.itens"
            :key="doc.ID"
            class="ocorrencia-item d-flex justify-content-between align-items-start mb-2 p-2 rounded"
          >
            <div>
              <b-badge variant="primary" class="mr-2">{{ doc.TIPO_DOC }}</b-badge>
              <span class="small text-muted">{{ formatarDataHora(doc.DT_UPLOAD) }}</span>
              <div class="small mt-1">
                <a :href="'/api/protected/motorista/documento/arquivo/' + doc.ID" target="_blank">
                  <i class="fa fa-eye mr-1"></i>{{ doc.NOME_ARQUIVO }}
                </a>
              </div>
              <div v-if="doc.USUARIO" class="small text-muted">por {{ doc.USUARIO }}</div>
            </div>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="excluirDocumento(doc)"
              :disabled="doc.excluindo"
            >
              <i :class="doc.excluindo ? 'fa fa-spinner fa-spin' : 'fa fa-trash'"></i>
            </b-button>
          </div>
        </div>

        <div class="d-flex justify-content-between mt-3">
          <b-button size="sm" variant="outline-primary" @click="abrirModalDocumento(listaDocumentos.ordem, listaDocumentos.pedido); $bvModal.hide('modal-lista-documentos')">
            <i class="fa fa-plus"></i> Novo documento
          </b-button>
          <b-button variant="secondary" @click="$bvModal.hide('modal-lista-documentos')">Fechar</b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
export default {
  name: 'DashboardMotorista',

  data () {
    return {
      funcionarioId: null,
      motorista: { NOME: '' },
      ordens: [],
      carregando: false,
      ordensAbertas: [],
      pedidosPorOrdem: {},

      // Filtro de status
      filtroStatus: '4',
      filtroStatusTemp: '4',
      statusOptions: [
        { value: null, text: 'TODAS' },
        { value: '1', text: 'EM EMBARQUE' },
        { value: '4', text: 'EMBARCADO' },
        { value: '5', text: 'ENTREGUE' }
      ],

      ocorrencia: {
        ordem: null,
        pedido: null,
        tipo: null,
        descricao: ''
      },
      salvandoOcorrencia: false,

      documento: {
        ordem: null,
        pedido: null,
        tipoDoc: 'CANHOTO',
        arquivo: null,
        preview: null,
        mimeType: null
      },
      enviandoDocumento: false,

      listaOcorrencias: {
        ordem: null,
        pedido: null,
        itens: [],
        carregando: false
      },

      listaDocumentos: {
        ordem: null,
        pedido: null,
        itens: [],
        carregando: false
      },

      tiposOcorrencia: [
        { value: null, text: '-- Selecione --' },
        { value: 'PARCIAL', text: 'Entrega parcial' },
        { value: 'DEVOLVIDO', text: 'Devolvido' },
        { value: 'RECUSADO', text: 'Recusado pelo cliente' },
        { value: 'AUSENTE', text: 'Cliente ausente' },
        { value: 'OUTROS', text: 'Outros' }
      ],

      tiposDocumento: [
        { value: 'CANHOTO', text: 'Canhoto' },
        { value: 'ROMANEIO', text: 'Romaneio' },
        { value: 'FOTO', text: 'Foto de entrega' },
        { value: 'OUTRO', text: 'Outro' }
      ]
    }
  },

  computed: {
    filtroStatusLabel () {
      if (!this.filtroStatus) return 'Todos'
      const op = this.statusOptions.find(o => o.value === this.filtroStatus)
      return op ? op.text : 'Filtro'
    },
    totalPedidos () {
      let total = 0
      Object.values(this.pedidosPorOrdem).forEach(lista => { total += lista.length })
      return total || this.ordens.reduce((acc, o) => acc + (o.TOTAL_PEDIDOS || 0), 0)
    },
    totalEntregues () {
      let total = 0
      Object.values(this.pedidosPorOrdem).forEach(lista => {
        total += lista.filter(p => this.isEntregue(p)).length
      })
      return total
    },
    percentualEntregue () {
      if (!this.totalPedidos) return 0
      return Math.min((this.totalEntregues / this.totalPedidos) * 100, 100)
    }
  },

  async created () {
    this.funcionarioId = window.localStorage.getItem('funcionarioId')
    if (!this.funcionarioId || this.funcionarioId === 'null' || this.funcionarioId === 'undefined') {
      this.funcionarioId = null
      return
    }
    await this.carregarMotorista()
    await this.carregarOrdens()
  },

  methods: {
    getIniciais (nome) {
      if (!nome) return 'M'
      const partes = nome.trim().split(' ')
      const iniciais = partes.length >= 2
        ? partes[0][0] + partes[1][0]
        : partes[0].slice(0, 2)
      return iniciais.toUpperCase()
    },

    formatarData (data) {
      if (!data) return ''
      const d = new Date(data)
      if (isNaN(d)) return ''
      return d.toLocaleDateString('pt-BR')
    },

    formatarDataHora (data) {
      if (!data) return ''
      const d = new Date(data)
      if (isNaN(d)) return ''
      return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    },

    getStatusVariant (status) {
      const map = { '0': 'secondary', '1': 'warning', '2': 'info', '3': 'primary', '4': 'success', '5': 'success' }
      return map[status] || 'secondary'
    },

    getStatusOrdemEntrega (pedido) {
      return String((pedido && pedido.ORDEM_ENTREGA_STATUS) || '').trim()
    },

    getSituacaoVariant (pedido) {
      const item = pedido && typeof pedido === 'object' ? pedido : null
      const situacao = item ? String(item.SITUACAO || '').trim() : String(pedido || '').trim()
      if (item && situacao === '1' && item.ORDEM_ENTREGA_ID) {
        const statusOrdem = this.getStatusOrdemEntrega(item)
        if (statusOrdem === '1') return 'primary'
        if (['2', '3', '4'].includes(statusOrdem)) return 'info'
        return 'primary'
      }
      const map = { '1': 'success', '4': 'warning', '6': 'success' }
      return map[situacao] || 'secondary'
    },

    isEntregue (pedido) {
      return pedido.OP_RETORNO_ENTREGA === '1' || pedido.OP_RETORNO_ENTREGA === 1
    },

    getOrdemKey (ordem) {
      if (!ordem) return ''
      return `ENTREGA-${ordem.ID}`
    },

    contarEntregues (ordem) {
      const lista = this.pedidosPorOrdem[this.getOrdemKey(ordem)]
      if (!lista) return 0
      return lista.filter(p => this.isEntregue(p)).length
    },

    ordemAberta (ordem) {
      return this.ordensAbertas.includes(this.getOrdemKey(ordem))
    },

    async toggleOrdem (ordem) {
      const key = this.getOrdemKey(ordem)
      const idx = this.ordensAbertas.indexOf(key)
      if (idx >= 0) {
        this.ordensAbertas.splice(idx, 1)
      } else {
        this.ordensAbertas.push(key)
        if (!this.pedidosPorOrdem[key]) {
          await this.carregarPedidos(ordem)
        }
      }
    },

    aplicarFiltro () {
      this.filtroStatus = this.filtroStatusTemp
      this.$bvModal.hide('modal-filtro-status')
      this.pedidosPorOrdem = {}
      this.ordensAbertas = []
      this.carregarOrdens()
    },

    async carregarMotorista () {
      try {
        const { data } = await this.$http.get(`/api/protected/funcionario/getById/${this.funcionarioId}`)
        const f = Array.isArray(data) ? data[0] : data
        if (f) this.motorista = f
      } catch (err) {
        this.motorista.NOME = window.localStorage.getItem('nomeusuario') || 'Motorista'
      }
    },

    async carregarOrdens () {
      this.carregando = true
      try {
        let url = `/api/protected/motorista/getOrdens?funcionarioId=${this.funcionarioId}`
        if (this.filtroStatus) url += `&status=${this.filtroStatus}`
        const { data } = await this.$http.get(url)
        this.ordens = data || []
      } catch (err) {
        this.$root.$bvToast.toast('Erro ao carregar ordens de entrega.', {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
      } finally {
        this.carregando = false
      }
    },

    async carregarPedidos (ordem) {
      const key = this.getOrdemKey(ordem)
      try {
        const { data } = await this.$http.get(`/api/protected/motorista/getPedidos/${ordem.ID}`)
        this.$set(this.pedidosPorOrdem, key, data || [])
      } catch (err) {
        this.$set(this.pedidosPorOrdem, key, [])
        this.$root.$bvToast.toast('Erro ao carregar pedidos da ordem.', {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
      }
    },

    // ===== Confirmar Entrega =====
    async confirmarEntrega (ordem, pedido) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Confirmar entrega do pedido #${pedido.SEQUENCIA} para ${pedido.NOME_CLIENTE}?`,
        { title: 'Confirmar Entrega', okVariant: 'success', okTitle: 'Confirmar', cancelTitle: 'Cancelar', centered: true }
      )
      if (!ok) return
      try {
        await this.$http.post('/api/protected/motorista/marcarEntregue', {
          ordemEntregaId: ordem.ID,
          vendaId: pedido.SEQUENCIA,
          usuario: window.localStorage.getItem('login')
        })
        this.$root.$bvToast.toast('Entrega confirmada!', {
          title: 'Sucesso', autoHideDelay: 2500, variant: 'success', appendToast: true
        })
        await this.carregarPedidos(ordem)
      } catch (err) {
        const msg = (err && err.response && err.response.data) || 'Erro ao confirmar entrega'
        this.$root.$bvToast.toast(msg, {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
      }
    },

    // ===== Ocorrências =====
    abrirModalOcorrencia (ordem, pedido) {
      this.ocorrencia = { ordem, pedido, tipo: null, descricao: '' }
      this.$bvModal.show('modal-ocorrencia')
    },

    async salvarOcorrencia () {
      if (!this.ocorrencia.tipo) return
      this.salvandoOcorrencia = true
      try {
        if ((this.ocorrencia.ordem.TIPO_ORDEM || '') === 'ENTREGA') {
          await this.$http.post('/api/protected/ordementrega/registrarOcorrenciaVenda', {
            ordemEntregaId: this.ocorrencia.ordem.ID,
            vendaId: this.ocorrencia.pedido.SEQUENCIA,
            tipoOcorrencia: this.ocorrencia.tipo,
            observacao: this.ocorrencia.descricao,
            usuario: window.localStorage.getItem('login')
          })
        } else {
          await this.$http.post('/api/protected/motorista/ocorrencia', {
            embarqueId: this.ocorrencia.ordem.ID,
            tipoOrdem: this.ocorrencia.ordem.TIPO_ORDEM || 'EXPEDICAO',
            vendaId: this.ocorrencia.pedido.SEQUENCIA,
            tipoOcorrencia: this.ocorrencia.tipo,
            descricao: this.ocorrencia.descricao,
            usuario: window.localStorage.getItem('login')
          })
        }
        this.$bvModal.hide('modal-ocorrencia')
        this.$root.$bvToast.toast('Ocorrência registrada!', {
          title: 'Sucesso', autoHideDelay: 2500, variant: 'success', appendToast: true
        })
        await this.carregarPedidos(this.ocorrencia.ordem)
      } catch (err) {
        const msg = (err && err.response && err.response.data) || 'Erro ao registrar ocorrência'
        this.$root.$bvToast.toast(msg, {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
      } finally {
        this.salvandoOcorrencia = false
      }
    },

    async abrirListaOcorrencias (ordem, pedido) {
      this.listaOcorrencias = { ordem, pedido, itens: [], carregando: true }
      this.$bvModal.show('modal-lista-ocorrencias')
      if ((ordem.TIPO_ORDEM || '') === 'ENTREGA') {
        this.listaOcorrencias.itens = pedido.OBS_RETORNO
          ? [{
            ID: `ENTREGA-${pedido.SEQUENCIA}`,
            TIPO_OCORRENCIA: 'OCORRENCIA',
            DESCRICAO: pedido.OBS_RETORNO,
            DT_OCORRENCIA: pedido.DT_LANCAMENTO_RETORNO,
            USUARIO: pedido.USUARIO_RETORNO,
            SOMENTE_LEITURA: true,
            excluindo: false
          }]
          : []
        this.listaOcorrencias.carregando = false
        return
      }
      try {
        const { data } = await this.$http.get(`/api/protected/motorista/ocorrencias/${pedido.SEQUENCIA}`)
        this.listaOcorrencias.itens = (data || []).map(o => ({ ...o, excluindo: false }))
      } catch (err) {
        this.listaOcorrencias.itens = []
      } finally {
        this.listaOcorrencias.carregando = false
      }
    },

    async excluirOcorrencia (oc) {
      const ok = await this.$bvModal.msgBoxConfirm('Excluir esta ocorrência?', {
        title: 'Confirmar', okVariant: 'danger', okTitle: 'Excluir', cancelTitle: 'Cancelar', centered: true
      })
      if (!ok) return
      this.$set(oc, 'excluindo', true)
      try {
        await this.$http.delete(`/api/protected/motorista/ocorrencia/${oc.ID}`)
        const idx = this.listaOcorrencias.itens.indexOf(oc)
        if (idx >= 0) this.listaOcorrencias.itens.splice(idx, 1)
        // Atualiza contador no pedido
        if (this.listaOcorrencias.pedido) {
          await this.carregarPedidos(this.listaOcorrencias.ordem)
        }
      } catch (err) {
        this.$root.$bvToast.toast('Erro ao excluir ocorrência.', {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
        this.$set(oc, 'excluindo', false)
      }
    },

    // ===== Documentos =====
    abrirModalDocumento (ordem, pedido) {
      this.documento = { ordem, pedido, tipoDoc: 'CANHOTO', arquivo: null, preview: null, mimeType: null }
      this.$bvModal.show('modal-documento')
    },

    onArquivoChange (e) {
      const file = e.target.files[0]
      if (!file) { this.documento.preview = null; this.documento.mimeType = null; return }
      this.documento.mimeType = file.type
      const reader = new FileReader()
      reader.onload = (ev) => { this.documento.preview = ev.target.result }
      reader.readAsDataURL(file)
    },

    async enviarDocumento () {
      if (!this.documento.arquivo) return
      this.enviandoDocumento = true
      try {
        const formData = new FormData()
        formData.append('arquivo', this.documento.arquivo)
        formData.append('tipoDoc', this.documento.tipoDoc)
        formData.append('tipoOrdem', this.documento.ordem.TIPO_ORDEM || 'EXPEDICAO')
        formData.append('usuario', window.localStorage.getItem('login'))
        await this.$http.post(
          `/api/protected/motorista/documento/upload/${this.documento.ordem.ID}/${this.documento.pedido.SEQUENCIA}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        this.$bvModal.hide('modal-documento')
        this.$root.$bvToast.toast('Documento enviado!', {
          title: 'Sucesso', autoHideDelay: 2500, variant: 'success', appendToast: true
        })
        await this.carregarPedidos(this.documento.ordem)
      } catch (err) {
        const msg = (err && err.response && err.response.data) || 'Erro ao enviar documento'
        this.$root.$bvToast.toast(msg, {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
      } finally {
        this.enviandoDocumento = false
      }
    },

    async abrirListaDocumentos (ordem, pedido) {
      this.listaDocumentos = { ordem, pedido, itens: [], carregando: true }
      this.$bvModal.show('modal-lista-documentos')
      try {
        const { data } = await this.$http.get(`/api/protected/motorista/documentos/${pedido.SEQUENCIA}`)
        this.listaDocumentos.itens = (data || []).map(d => ({ ...d, excluindo: false }))
      } catch (err) {
        this.listaDocumentos.itens = []
      } finally {
        this.listaDocumentos.carregando = false
      }
    },

    async excluirDocumento (doc) {
      const ok = await this.$bvModal.msgBoxConfirm('Excluir este documento?', {
        title: 'Confirmar', okVariant: 'danger', okTitle: 'Excluir', cancelTitle: 'Cancelar', centered: true
      })
      if (!ok) return
      this.$set(doc, 'excluindo', true)
      try {
        await this.$http.delete(`/api/protected/motorista/documento/${doc.ID}`)
        const idx = this.listaDocumentos.itens.indexOf(doc)
        if (idx >= 0) this.listaDocumentos.itens.splice(idx, 1)
        if (this.listaDocumentos.pedido) {
          await this.carregarPedidos(this.listaDocumentos.ordem)
        }
      } catch (err) {
        this.$root.$bvToast.toast('Erro ao excluir documento.', {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
        this.$set(doc, 'excluindo', false)
      }
    },

    // ===== Confirmar Entrega Total da Ordem =====
    async confirmarEntregaOrdem (ordem) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Confirmar que todos os pedidos da Ordem #${ordem.NUMERO} foram entregues?`,
        { title: 'Finalizar Entrega', okVariant: 'success', okTitle: 'Confirmar', cancelTitle: 'Cancelar', centered: true }
      )
      if (!ok) return
      try {
        await this.$http.post('/api/protected/ordemexpedicao/marcarEmbarqueEntregue', {
          embarqueId: ordem.ID,
          usuario: window.localStorage.getItem('login')
        })
        this.$root.$bvToast.toast(`Ordem #${ordem.NUMERO} finalizada como ENTREGUE!`, {
          title: 'Sucesso', autoHideDelay: 3000, variant: 'success', appendToast: true
        })
        await this.carregarOrdens()
      } catch (err) {
        const msg = (err && err.response && err.response.data) || 'Erro ao finalizar ordem'
        this.$root.$bvToast.toast(msg, {
          title: 'Erro', autoHideDelay: 3000, variant: 'danger', appendToast: true
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-motorista {
  padding: 10px;
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Profile */
.profile-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #1a202c;
  padding: 18px 20px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
  border: 1px solid #edf2f7;

  .profile-info {
    .name {
      font-weight: 700;
      color: #2d3748;
      font-size: 1.2rem;
      letter-spacing: -0.5px;
    }
    .meta-label {
      font-size: 0.72rem;
      color: #718096;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 600;
      span {
        font-weight: 800;
        color: #2d3748;
        margin-left: 4px;
      }
    }
  }

  .progress-container {
    margin-top: 10px;
    .small {
      color: #718096;
      font-weight: 500;
      font-size: 0.75rem;
    }
  }
}

.progress-modern {
  border-radius: 12px;
  background-color: #edf2f7;
}

/* Ordem Card */
.ordem-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #edf2f7;
}

.ordem-card-header {
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;

  .ordem-numero {
    font-weight: 700;
    font-size: 0.95rem;
    color: #2d3748;
  }
  .ordem-data {
    font-size: 0.75rem;
  }
}

.ordem-placa {
  border-top: 1px solid #f1f5f9;
  background-color: #f8fafc;
}

/* Status colors — herdam da paleta do DashboardVendedor */
.status-success { background-color: #f0fff4; color: #38a169; border-bottom: 1px solid #c6f6d5; }
.status-danger  { background-color: #fff5f5; color: #e53e3e; border-bottom: 1px solid #fed7d7; }
.status-warning { background-color: #fffaf0; color: #dd6b20; border-bottom: 1px solid #feebc8; }
.status-info    { background-color: #ebf8ff; color: #3182ce; border-bottom: 1px solid #bee3f8; }
.status-primary { background-color: #f5f3ff; color: #7c3aed; border-bottom: 1px solid #ddd6fe; }
.status-secondary { background-color: #f7fafc; color: #4a5568; border-bottom: 1px solid #edf2f7; }

/* Pedido Card */
.pedidos-list {
  background-color: #f8fafc;
  padding: 8px 8px 4px;
}

.pedido-card {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #edf2f7;
}

.pedido-header {
  padding: 4px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.6px;
}

.pedido-body {
  padding: 8px 10px;

  .pedido-cliente {
    font-weight: 700;
    color: #1a202c;
    font-size: 0.9rem;
  }
  .pedido-info {
    font-size: 0.72rem;
    color: #718096;
    margin-top: 2px;

    .pedido-id { font-weight: 600; }
  }
  .pedido-endereco {
    font-size: 0.72rem;
    margin-top: 3px;
  }
}

.pedido-acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge-ocorrencia {
  background-color: #fffaf0;
  color: #dd6b20;
  border: 1px solid #feebc8;
  border-radius: 8px;
  padding: 1px 5px;
  font-size: 0.65rem;
  font-weight: 700;
  cursor: pointer;
  opacity: 0.5;
  user-select: none;
  &.has-items { opacity: 1; }
}

.badge-doc {
  background-color: #ebf8ff;
  color: #3182ce;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 1px 5px;
  font-size: 0.65rem;
  font-weight: 700;
  cursor: pointer;
  opacity: 0.5;
  user-select: none;
  &.has-items { opacity: 1; }
}

/* Pedido entregue — fundo verde suave */
.pedido-entregue {
  border: 1.5px solid #9ae6b4 !important;

  .pedido-header {
    background-color: #f0fff4 !important;
    color: #276749 !important;
    border-bottom-color: #9ae6b4 !important;
  }

  .pedido-cliente {
    color: #276749;
  }
}

/* Itens das listas de ocorrência / documento */
.ocorrencia-item {
  background-color: #f8fafc;
  border: 1px solid #edf2f7;
}

/* Imagem Preview */
.img-preview {
  max-height: 200px;
  object-fit: contain;
  border-radius: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/DashboardMotorista.vue